<template>
  <header class="header-contact _ui-grid">
    <h1 class="title text-3xl">
      {{ data.title }}
    </h1>
    <div class="infos-container">
      <ContactCard
        class="card"
        v-if="data.contactCard"
        :data="data.contactCard"
      />
      <div v-if="siteInfo.address" class="address">
        <p class="title text-xs">Adresse</p>
        <div class="inner">
          <StructuredText
            :data="siteInfo.address"
            class="text structured-text text-sm"
          />
          <Directions class="directions underline text-xs" />
        </div>
      </div>
      <div class="contact">
        <p class="title text-xs">Kontakt</p>
        <div class="inner">
          <a class="text-md link" :href="`tel:${siteInfo.telephone}`">{{
            siteInfo.telephone
          }}</a>
          <a class="email text-sm link" :href="`mailto:${siteInfo.email}`">{{
            siteInfo.email
          }}</a>
        </div>
      </div>
    </div>
    <Glow />
  </header>
</template>

<script setup lang="ts">
import { StructuredText } from 'vue-datocms';
import Directions from '../partials/Directions.vue';
import ContactCard from '../partials/ContactCard.vue';
import type { HeaderContactFragment, SiteInfoFragment } from '#gql';
import Glow from '@/components/site-header/Glow.vue';

defineProps<{
  data: HeaderContactFragment;
}>();

const siteInfo = useNuxtApp().$siteInfo as SiteInfoFragment;
</script>

<style scoped lang="scss">
.header-contact {
  position: relative;
  width: 100%;
  padding-block: 12.5rem 5rem;

  text-align: left;
  row-gap: 6rem;

  @media (--vs) {
    padding-inline: 1.25rem !important;
  }

  > .title {
    max-width: 25ch;
    grid-row: 1/2;
    grid-column: 2/-2;
  }

  .infos-container {
    grid-row: 2/3;
    grid-column: 2/-2;
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr 1fr 1fr;
  }

  > .card {
    min-width: 28ch;
    margin-right: auto;
  }

  > .text {
    padding-top: 2.5rem;
    padding-bottom: 3.25rem;
    max-width: 40ch;
    text-align: left;
    margin: 0 auto 0 0;
  }

  > .picture,
  > .video {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    overflow: hidden;

    :deep(img) {
      object-fit: cover;
    }
  }

  .card,
  .address,
  .contact {
    background: var(--base-color-white-40);
    padding: 2rem;
    border-radius: var(--base-border-radius-small);
    width: 100%;
  }

  .address,
  .contact {
    display: flex;
    flex-direction: column;
    row-gap: 1.5rem;
  }

  .address {
    margin-left: auto;

    .inner {
      display: flex;
      flex-direction: column;
      row-gap: 3.375rem;
    }
  }

  .contact {
    margin-right: auto;
    height: fit-content;

    .inner {
      display: flex;
      flex-direction: column;
      a {
        margin-right: auto;
        text-decoration: none;
      }
      a.email {
        position: relative;
      }
      a.email::after,
      a.email::before {
        content: '';
        position: absolute;
        width: 100%;
        left: 0;
        bottom: 0;
        height: 2px;
        background-color: rgba(9, 89, 139, 0.2);
        transition: transform 0.3s ease-in-out;
      }
      a.email::after {
        transform: scaleX(0);
        transform-origin: left;
        background-color: var(--color-blue-500);
      }
      a.email:hover {
        &::after {
          transform: scaleX(1);
        }
      }
    }
  }
}

@media (--vs) {
  .header-contact {
    padding: 7.75rem 0 0 0;
    row-gap: 2.5rem;

    .infos-container {
      grid-template-columns: 1fr;
      background: unset;
      row-gap: 2.5rem;
      grid-template-areas: 'card' 'contact' 'address';
    }

    .contact-card {
      background: var(--base-color-white-80);
      grid-area: card;
    }

    .card {
      padding: 1rem;
    }

    .contact {
      grid-area: contact;
      .inner {
        a.email {
          border-bottom: 2px solid var(--base-color-foreground-light);
        }
      }
    }

    .address {
      grid-area: address;
    }

    .address,
    .contact {
      row-gap: 0.5rem;
      background: var(--base-color-white-80);
      padding: 1rem;
    }

    .directions {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.video {
  .video-js {
    aspect-ratio: 16/9;
  }
}
</style>
